// External imports
import React from 'react';

import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  withStyles,
  TextField,
  Tooltip,
  MenuItem,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import InfoIcon from '@material-ui/icons/Info';
// Internal imports
import ClickableTypography from '#/Common/ClickableTypography.jsx';
import { LOGIN_SETTINGS, TVA_LANGUAGE_OPTIONS } from '~/app/constants.js';
// Styling imports
import { styles } from '~/app/Components/Settings/LoginSettings/css/loginSettings.js';
import '~/app/Components/Settings/LoginSettings/css/loginSettings.css';

const LoginSettings = props => {
  const { classes } = props;

  const setInitialSettings = (settings, getSettingsObj) => {
    const initialSettings = [];
    for (const setting in settings) {
      const obj = getSettingsObj(setting);
      if (obj.value && obj.text) {
        initialSettings.push(setting);
      }
    }
    return initialSettings;
  };

  const getSettingsObject = setting => {
    return typeof props.loginSettings[setting] !== 'object'
      ? JSON.parse(props.loginSettings[setting])
      : props.loginSettings[setting];
  };

  const initialSettings = setInitialSettings(props.loginSettings, getSettingsObject);

  const [checked, setChecked] = React.useState(initialSettings);

  const handleToggle = setting => {
    if (!(setting === 'onScreen')) {
      const settingObj = getSettingsObject(setting);
      const checkedSettings = [...checked];
      const newSettings = props.loginSettings;

      // Only allow one when selecting signature pad type
      if (settingObj.value === false) {
        checkedSettings.push(setting);
        setChecked(checkedSettings);
        settingObj.value = true;
        // If the user changes the signature pad type, we need to remove the other checkboxes
      } else {
        checkedSettings.splice(setting, 1);
        setChecked(checkedSettings);
        settingObj.value = false;
      }

      newSettings[setting] = settingObj;
      props.setLoginSettings(newSettings);
    } else {
      // Always get the three different setting signature pads
      const settingObjOnScreen = getSettingsObject('onScreen');
      const checkedSettings = [...checked];
      const newSettings = props.loginSettings;

      if (setting === 'onScreen') {
        if (settingObjOnScreen.value === false) {
          // Checking the on-screen option
          checkedSettings.push(setting);
          setChecked(checkedSettings);
          settingObjOnScreen.value = true;
        } else {
          // Checking the on-screen option
          checkedSettings.push(setting);
          setChecked(checkedSettings);
          settingObjOnScreen.value = false;
        }
      }
      newSettings.onScreen = settingObjOnScreen;
      props.setLoginSettings(newSettings);
    }
  };

  const handleTVAChange = e => {
    const value = e.target.value;
    const settingObj = getSettingsObject('tvap');
    const newSettings = props.loginSettings;
    settingObj.value = value;
    newSettings.tvap = settingObj;
    props.setLoginSettings(newSettings);
  };

  const setupLoginSettingsOptions = () => {
    // SHOW_IRS_MESSAGE is deliberately no longer excluded (CORP-1199). It was,
    // which meant a preparer who dismissed the IRS Security Message had no way to
    // turn it back on — a parity gap with Desktop, which has that checkbox here.
    // It renders now that addNewSettingsFields normalizes it to {value, text};
    // as a bare boolean it fell through the typeof check below and rendered
    // nothing at all.
    //
    // SHOW_PRIOR_YEAR_FEE_NOTICE needs no entry here: it is already shaped
    // {value, text}, so this map picks it up on its own.
    return Object.keys(props.loginSettings)
      .filter(x => x !== LOGIN_SETTINGS.SHOW_ON_SCREEN && x !== LOGIN_SETTINGS.SHOW_TVA_LANG)
      .map(setting => {
        const settingObj = getSettingsObject(setting);
        if (typeof settingObj !== 'object') return null; // some elements in settingObj are not meant to be rendered to UI.

        return (
          <ListItem key={setting + 'newChk'}>
            <Checkbox
              id={setting + 'chk'}
              name={setting}
              classes={{ root: classes.loginPreferencesCheckbox }}
              onClick={() => handleToggle(setting)}
              checked={settingObj.value === true}
              icon={
                <CheckBoxOutlineBlankIcon
                  classes={{ root: classes.loginPreferencesCheckboxOutlineIcon }}
                  color="secondary"
                />
              }
              checkedIcon={
                <CheckBoxIcon
                  classes={{ root: classes.loginPreferencesCheckboxIconSize }}
                  color="primary"
                />
              }
            />
            <ListItemText
              classes={{ root: classes.loginPreferencesCheckboxItem }}
              primary={settingObj.text}
              onClick={() => handleToggle(setting)}
            />
          </ListItem>
        );
      });
  };

  const returnLanguageList = () => {
    return TVA_LANGUAGE_OPTIONS.map((option, i) => (
      <MenuItem key={`${i}-${option}`} value={option}>
        {option}
      </MenuItem>
    ));
  };

  return (
    <div>
      <div className="modal-header">
        Login Preferences
        <ClickableTypography color="inherit" onClick={() => props.handleClose(false, true)}>
          X
        </ClickableTypography>
      </div>
      <List>{setupLoginSettingsOptions()}</List>
      <hr className="login-preferences-hr" />
      <div className="login-preferences-section-heading">
        <span>AI Language Preference</span>
        <Tooltip title="Select Language for AI FormAssist. Only available for Tax Year 2025 and onwards.">
          <InfoIcon fontSize="small" />
        </Tooltip>
      </div>
      <div className="login-preferences-select">
        <TextField
          select
          disabled={props.season < 2026}
          variant="outlined"
          fullWidth
          id="tvaLanguageSelection"
          name="tvaLanguage"
          value={props.loginSettings.tvap.value}
          onChange={handleTVAChange}
        >
          {returnLanguageList()}
        </TextField>
      </div>
      <hr className="login-preferences-hr" />
      <div className="login-references-btn-container">
        <Button
          id="btnCancel"
          size="small"
          color="primary"
          classes={{ root: classes.loginPreferencesBtn }}
          onClick={() => props.handleClose(false, true)}
        >
          Cancel
        </Button>
        <Button
          id="btnSave"
          size="small"
          classes={{ root: classes.loginPreferencesBtn }}
          variant="contained"
          color="primary"
          onClick={props.handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(LoginSettings);
